import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { fetchBot } from '@/services/services';
import { ListRange, listRangeBounds } from '@/components/Hub/listRange';
import { chunk } from '../services/pacQueueHydration';

/** The Won ledger for a range, from the payments backend through the bot (BAR-2804), plus each patient. */

const BOT_NAME = 'create-treatment-payment';

export interface WonEvent {
  id: string;
  /** Null for a user bariendo-be has no Medplum Patient for. */
  medplumPatientId: string | null;
  kind: 'WON' | 'REVERSAL';
  track: string;
  benefitAdministrator: string | null;
  occurredAt: string;
  reversesId: string | null;
}

export interface WonEventsState {
  events: WonEvent[];
  /** The backend stopped at its cap; older events in the range are missing. */
  hasMore: boolean;
  patientById: Map<string, Patient>;
  loading: boolean;
  error: string | undefined;
}

const EMPTY: Omit<WonEventsState, 'loading'> = {
  events: [],
  hasMore: false,
  patientById: new Map(),
  error: undefined,
};

export function useWonEvents(range: ListRange): WonEventsState {
  const medplum = useMedplum();
  const [state, setState] = useState<Omit<WonEventsState, 'loading'>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const { start, end } = listRangeBounds(range);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function load(): Promise<void> {
      const { won } = await fetchBot<{ won: { events: WonEvent[]; hasMore: boolean } }>(medplum, BOT_NAME, {
        operation: 'wonList',
        from: start,
        to: end,
      });
      const ids = [
        ...new Set(won.events.map((event) => event.medplumPatientId).filter((id): id is string => Boolean(id))),
      ];
      const patients = (
        await Promise.all(
          chunk(ids).map((idChunk) => medplum.searchResources('Patient', { _id: idChunk.join(','), _count: '1000' }))
        )
      ).flat();

      if (!cancelled) {
        setState({
          events: won.events,
          hasMore: won.hasMore,
          patientById: new Map(patients.map((patient) => [patient.id as string, patient])),
          error: undefined,
        });
      }
    }

    load()
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ ...EMPTY, error: error instanceof Error ? error.message : 'Could not load Won' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, start, end]);

  return { ...state, loading };
}
